import {FormattableString} from "../FormattableString";
import readNestedProperty from "../helpers/readNestedProperty";
import Locale, {DateFormat, DatetimeFormat, NumberFormat, TimeFormat} from "./Locale";
import IStringFormatter from "./IStringFormatter.js";

const _keyed: RegExp = /{(\w+)(:(\w+))?}/g;

function guessValueType(value: any): string | null {
	if (typeof value === "number") {
		return "n";
	}
	return value instanceof Date ? "d" : null;
}

function getNumberFormat(subType: string | null): NumberFormat {
	switch (subType) {
		case "i": {
			return "integer";
		}
		case "p": {
			return "percent";
		}
		case "c": {
			return "currency";
		}
		default: {
			return "decimal";
		}
	}
}

function getDateFormat(subType: string | null): DateFormat {
	switch (subType) {
		case "s": {
			return "short";
		}
		case "m": {
			return "medium";
		}
		case "l": {
			return "long";
		}
		case "f": {
			return "full";
		}
		case "d": {
			return "day_month";
		}
		case "y": {
			return "smonth_year";
		}
		case "Y": {
			return "month_year";
		}
		default: {
			return "default";
		}
	}
}

function getDateTimeFormat(subType: string | null): DatetimeFormat {
	switch (subType) {
		case "s": {
			return "short";
		}
		case "m": {
			return "medium";
		}
		case "l": {
			return "long";
		}
		case "f": {
			return "full";
		}
		default: {
			return "default";
		}
	}
}

function getTimeFormat(subType: string | null): TimeFormat {
	switch (subType) {
		case "s": {
			return "short";
		}
		case "m": {
			return "medium";
		}
		case "l": {
			return "long";
		}
		case "f": {
			return "full";
		}
		default: {
			return "default";
		}
	}
}

function valueFormatter(culture: Locale, value: any, format?: string | null | undefined): string {
	//[n]umber -   [i]nteger [p]ercent, [c]urrency
	//[d]ate -     [s]hort, [m]edium, [l]ong, [f]ull, [d]ayMonth, MonthShort[y]ear, Month[Y]ear
	//[t]ime -     [s]hort, [m]edium, [l]ong, [f]ull
	//[D]ateTime - [s]hort, [m]edium, [l]ong, [f]ull

	let type: string | null;
	let subType = null;

	if (format == null || format.length === 0) {
		type = guessValueType(value);
	} else {
		let segments = format.split("_");
		type = segments[0];
		subType = segments[1];
	}

	switch (type) {
		case "n": {
			return culture.formatNumber(+value, getNumberFormat(subType));
		}
		case "d": {
			return culture.formatDate(value, getDateFormat(subType));
		}
		case "D": {
			return culture.formatDateTime(value, getDateTimeFormat(subType));
		}
		case "t": {
			return culture.formatTime(value, getTimeFormat(subType));
		}
		default: {
			return value.toString();
		}
	}
}

export default class SimpleStringFormatter implements IStringFormatter {
	private readonly _culture: Locale;

	constructor(culture: Locale) {
		this._culture = culture;
	}


	format(message: FormattableString): string;
	format(message: string, args?: Record<string, any> | null | undefined): string;
	format(message: unknown, args?: unknown): string {
		if (message == null) {
			return "";
		}

		let text: string;
		let params: Record<string, any> | null | undefined;

		if (typeof message === "string") {
			text = message;
			params = args as Record<string, any> | null;
		} else {
			text = (message as FormattableString).text;
			params = (message as FormattableString).args;
		}

		if (params == null || (Object.keys(params)).length === 0) {
			return text;
		}

		return text.replace(_keyed, (match, key, _, format) => params.hasOwnProperty(key) ? valueFormatter(this._culture, readNestedProperty(params, key), format) : match);
	}
}
